import { LiveUpdate } from '@/components/LiveUpdate'
import * as D from '@/domain'
import {
  CantonId,
  CantonState,
  caseBallotState,
  caseMajorityOfCantonsVote,
  caseOutcome,
  Intl,
  MunicipalityId,
  unPercent,
} from '@/domain'
import { CantonalBallot } from '@/domain/cantonal-ballot'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useEnv } from '@/lib/env'
import { BallotVariant, breakpoints as bp, color, Icon, mkFont, pickDivergingMain, plexBold } from '@/materials'
import styled from '@emotion/styled'
import { BREAKPOINTS } from '@ta-interaktiv/react-components'
import { option as O } from 'fp-ts'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useBallotContext } from '../../contexts'
import { addThinSpacePercentage } from '../../elements'
import { cantonDescriptor, commonDescriptors } from '../../locale/messages'
import { TieBreakRow } from '../ballot-teaser/internal'
import * as F from 'fp-ts'

/*
 * The tag colors / texts depend only on the ballot state, they are not affected
 * by majorityOfCantons
 */
export const mkTags = ({ formatMessage }: Intl, variant: BallotVariant) => {
  const [yea, nay] = pickDivergingMain(variant)
  const messages = {
    default: {
      yea: commonDescriptors.yea,
      nay: commonDescriptors.nay,
      projectionYea: commonDescriptors.projectionYea,
      projectionNay: commonDescriptors.projectionNay,
      finalResultYea: commonDescriptors.finalResultYea,
      finalResultNay: commonDescriptors.finalResultNay,
    },
    tieBreak: {
      yea: commonDescriptors.tieBreakBlank,
      nay: commonDescriptors.tieBreakBlank,
      projectionYea: commonDescriptors.projectionInitiative,
      projectionNay: commonDescriptors.projectionCounterproposal,
      finalResultYea: commonDescriptors.finalResultInitiative,
      finalResultNay: commonDescriptors.finalResultCounterproposal,
    },
  }[variant]

  return caseBallotState({
    pre: {
      tagL: {
        color: color.lightGrey,
        text: formatMessage(messages.yea),
      },
      tagR: {
        color: color.lightGrey,
        text: formatMessage(messages.nay),
      },
    },
    inProgress: {
      tagL: {
        color: color.lightGrey,
        text: formatMessage(messages.yea),
      },
      tagR: {
        color: color.lightGrey,
        text: formatMessage(messages.nay),
      },
    },
    trend: ({ trend }) => ({
      tagL: {
        color: caseOutcome({ yea: yea, nay: color.lightGrey })(trend),
        text: formatMessage(messages.yea),
      },
      tagR: {
        color: caseOutcome({ yea: color.lightGrey, nay: nay })(trend),
        text: formatMessage(messages.nay),
      },
    }),
    projection: ({ projection }) => ({
      tagL: {
        color: yea,
        text: formatMessage(messages.projectionYea, {
          projection: unPercent(projection),
        }),
      },
      tagR: {
        color: nay,
        text: formatMessage(messages.projectionNay, {
          projection: 1 - unPercent(projection),
        }),
      },
    }),
    finalResult: ({ finalResult }) => ({
      tagL: {
        color: yea,
        isBold: finalResult.accepted,
        text: formatMessage(messages.finalResultYea, {
          finalResult: unPercent(finalResult.votes.yea.percent),
        }),
      },
      tagR: {
        color: nay,
        isBold: !finalResult.accepted,
        text: formatMessage(messages.finalResultNay, {
          finalResult: unPercent(finalResult.votes.nay.percent),
        }),
      },
    }),
  })
}

const FederalTopRoot = styled('div')`
  margin-bottom: 12px;

  font-family: var(--font-sans-serif);
  font-size: 15px;
  line-height: 23px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`

export interface FederalTopProps {
  summary: D.FederalBallotSummary
  selectedCantonId: O.Option<CantonId>
  cantonState: O.Option<CantonState>
  ballotState: D.BallotState
}

export const FederalTop = React.memo<FederalTopProps>((props) => {
  const { intl } = useEnv()
  const { ballotVariant } = useBallotContext()
  const { summary, selectedCantonId, cantonState } = props
  const [yea, nay] = pickDivergingMain(ballotVariant)

  interface Parts {
    topL: React.ReactNode
    topR: React.ReactNode

    tagL: { color: string; text: React.ReactNode }
    tagR: { color: string; text: React.ReactNode }

    center: React.ReactNode
  }

  const liveUpdate = (info?: React.ReactNode) => (
    <EndResultContainer>
      <LiveUpdateContainer>
        <LiveUpdate />
      </LiveUpdateContainer>
      {info ?? <FormattedMessage id="mkzSfkH" defaultMessage="Liveprognose" />}
    </EndResultContainer>
  )

  const { topR, tagL, tagR, center } = F.function.pipe(
    cantonState,
    F.option.fold(
      /*
       * No canton selected
       */
      () => {
        const t = mkTags(intl, ballotVariant)(summary.state.ballotState)

        const { topL, topR } = D.caseBallotState<{ topL: React.ReactNode; topR: React.ReactNode }>({
          pre: { topL: null, topR: null },
          inProgress: { topL: liveUpdate(), topR: null },
          trend: () => ({ topL: liveUpdate(), topR: null }),
          projection: () => ({ topL: liveUpdate(), topR: null }),
          finalResult: ({ finalResult }) => {
            const {
              voterTurnout: { percent },
            } = finalResult
            const outcome = D.resultOutcome(finalResult)

            return {
              topL: (
                <span>
                  <FederalTopParticipationIcon>
                    <Icon name="Checkmark18" />
                  </FederalTopParticipationIcon>
                  <FormattedMessage
                    id="Dqco1ku"
                    defaultMessage="<optional>Endresultat: </optional>{outcome}"
                    values={{
                      optional: (children: React.ReactNode) => {
                        return <Optional>{children}</Optional>
                      },
                      outcome: (
                        <span
                          style={{
                            color: D.caseOutcome({ yea: color.blue[700], nay: color.red[700] })(outcome),
                            fontWeight: 'bold',
                          }}
                        >
                          {intl.formatMessage(commonDescriptors.finalResultState, {
                            outcome,
                          })}
                        </span>
                      ),
                    }}
                  />
                </span>
              ),
              topR: percent && (
                <span>
                  {addThinSpacePercentage(
                    intl.formatMessage(commonDescriptors.voterTurnout, {
                      percent: unPercent(percent),
                    }),
                  )}
                </span>
              ),
            }
          },
        })(summary.state.ballotState)

        return caseMajorityOfCantonsVote<Parts>({
          notRequired: {
            topL,
            topR,
            ...t,
            center: <FederalCenter selectedCantonId={selectedCantonId} />,
          },
          undecided: {
            topL,
            topR,
            ...t,
            center: <FederalCenter selectedCantonId={selectedCantonId} />,
          },
          accept: {
            topL,
            topR,
            ...t,
            center: <FederalCenter selectedCantonId={selectedCantonId} />,
          },
          reject: {
            topL,
            topR,
            tagL: {
              color: yea,
              text: t.tagL.text,
            },
            tagR: {
              color: nay,
              text: t.tagR.text,
            },
            center: <FederalCenter selectedCantonId={selectedCantonId} />,
          },
        })(summary.state.majorityOfCantons)
      },

      /*
       * A canton selected.
       */
      (cantonState) => {
        const t = mkTags(intl, ballotVariant)(cantonState.state)

        // The optional canton state info text. It is NOT used when the cantonal ballot state
        // is final result!
        const optInfo = F.function.pipe(
          D.optionMultiLocaleString(intl, cantonState.info),
          F.option.filter((x) => x !== ''),
        )
        const emptyOrInfo = F.function.pipe(
          optInfo,
          F.option.foldW(
            () => null,
            (x) => x,
          ),
        )

        const { topL, topR } = D.caseBallotState<{ topL: React.ReactNode; topR: React.ReactNode }>({
          pre: { topL: null, topR: null },
          inProgress: { topL: liveUpdate(emptyOrInfo), topR: null },
          trend: () => ({ topL: liveUpdate(emptyOrInfo), topR: null }),
          projection: ({ detail }) => ({
            topL: liveUpdate(
              F.function.pipe(
                optInfo,
                F.option.foldW(
                  () => D.pickMultiLocaleString(intl, detail, intl.formatMessage(commonDescriptors.projection)),
                  (x) => x,
                ),
              ),
            ),
            topR: null,
          }),
          finalResult: ({ finalResult }) => {
            const {
              voterTurnout: { percent },
            } = finalResult
            const outcome = D.resultOutcome(finalResult)

            return {
              topL: (
                <span>
                  <FederalTopParticipationIcon>
                    <Icon name="Checkmark18" />
                  </FederalTopParticipationIcon>
                  <FormattedMessage
                    id="Dqco1ku"
                    defaultMessage="<optional>Endresultat: </optional>{outcome}"
                    values={{
                      optional: (children: React.ReactNode) => {
                        return <Optional>{children}</Optional>
                      },
                      outcome: (
                        <span
                          style={{
                            color: D.caseOutcome({ yea: color.blue[700], nay: color.red[700] })(outcome),
                            fontWeight: 'bold',
                          }}
                        >
                          {intl.formatMessage(commonDescriptors.finalResultState, {
                            outcome,
                          })}
                        </span>
                      ),
                    }}
                  />
                </span>
              ),
              topR: percent && (
                <span>
                  {addThinSpacePercentage(
                    intl.formatMessage(commonDescriptors.voterTurnout, {
                      percent: unPercent(percent),
                    }),
                  )}
                </span>
              ),
            }
          },
        })(cantonState.state)

        return {
          topL,
          topR,
          ...t,
          center: <FederalCenter selectedCantonId={selectedCantonId} />,
        }
      },
    ),
  )

  return (
    <>
      <FederalTopRoot>{topR}</FederalTopRoot>
      <Top tagL={tagL} center={center} tagR={tagR} />
    </>
  )
})

export interface CantonalTopProps {
  cantonalBallot: CantonalBallot
  selectedMunicipalityId: O.Option<MunicipalityId>
}

export const CantonalTop = React.memo<CantonalTopProps>((props) => {
  const { intl } = useEnv()
  const { ballotVariant } = useBallotContext()
  const { cantonalBallot, selectedMunicipalityId } = props

  const ballotState = F.function.pipe(
    selectedMunicipalityId,
    F.option.chain((id) => O.fromNullable(cantonalBallot.municipalities.find((x) => x.id === id))),
    F.option.fold(
      () => cantonalBallot.ballotState,
      (cs) => cs.ballotState,
    ),
  )

  const { tagL, tagR } = mkTags(intl, ballotVariant)(ballotState)

  const center = <CantonalCenter cantonalBallot={cantonalBallot} selectedMunicipalityId={selectedMunicipalityId} />

  return (
    <>
      <CantonalTopRoot>
        <EndResult overrideableBallotState={D.mkOverrideableBallotState({ ballotState, outcome: undefined })} />
      </CantonalTopRoot>
      <Top tagL={tagL} center={center} tagR={tagR} />
    </>
  )
})

const Optional = styled('span')`
  @media only screen and (max-width: ${BREAKPOINTS.tablet}px) {
    display: none;
  }
`

const CantonalTopRoot = styled('div')`
  margin-bottom: 12px;
`

interface TopProps {
  tagL: { color: string; text: React.ReactNode; isBold?: boolean }
  center: React.ReactNode
  tagR: { color: string; text: React.ReactNode; isBold?: boolean }
}

export const Top = ({ tagL, center, tagR }: TopProps) => {
  return (
    <>
      <TopRoot>
        <Tag style={{ color: tagL.color, fontWeight: tagL.isBold ? 'bold' : 'normal' }}>{tagL.text}</Tag>
        {center}
        <Tag style={{ color: tagR.color, fontWeight: tagR.isBold ? 'bold' : 'normal' }}>{tagR.text}</Tag>
      </TopRoot>
      <TieBreakRow />
    </>
  )
}

interface FederalCenterProps {
  selectedCantonId: O.Option<CantonId>
}

const FederalCenter = React.memo<FederalCenterProps>((props) => {
  const { intl } = useEnv()
  const { formatMessage } = intl
  const { selectedCantonId } = props

  /**
   * no canton selected -> show municipality name
   * otherwise -> show canton name
   */
  return F.function.pipe(
    selectedCantonId,
    F.option.fold(
      () => <Center short={formatMessage(commonDescriptors.chTitle)} full={formatMessage(commonDescriptors.chTitle)} />,
      (id) => (
        <Center
          short={formatMessage(cantonDescriptor(id, 'short'))}
          full={formatMessage(cantonDescriptor(id, 'full'))}
        />
      ),
    ),
  )
})

interface CantonalCenterProps {
  cantonalBallot: CantonalBallot
  selectedMunicipalityId: O.Option<MunicipalityId>
}

const CantonalCenter = React.memo<CantonalCenterProps>((props) => {
  const { cantonalBallot, selectedMunicipalityId } = props

  return F.function.pipe(
    selectedMunicipalityId,
    F.option.fold(
      () => {
        /* zero width no-break space */
        const nbsp = <>&#xfeff;</>
        return <Center short={nbsp} full={nbsp} />
      },

      /* Selected canton: show name */
      (id) => {
        const selectedMunicipality = O.fromNullable(cantonalBallot.municipalities.find((x) => x.id === id))

        return F.function.pipe(
          selectedMunicipality,
          F.option.foldW(
            () => null,
            ({ name }) => <Center short={name} full={name} />,
          ),
        )
      },
    ),
  )
})

interface CenterProps {
  short: React.ReactNode
  full: React.ReactNode
}

export const Center = ({ short, full }: CenterProps) => {
  const mediumUp = useMediaQuery('(min-width: 640px)')

  return <TopCenter>{mediumUp ? full : short}</TopCenter>
}

export const fontBallotResultPaneTop = mkFont(
  {
    name: 'BallotResultPaneTop',
    fontFace: plexBold,
    cssProperties: {
      fontSize: '20',
      lineHeight: '1.3',
    },
  },
  {
    fontSize: ['15px', '20px', '20px', '20px'],
  },
)

const TopRoot = styled('div')`
  ${fontBallotResultPaneTop};
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 6px;
`

const TopCenter = styled('div')`
  position: absolute;
  width: 100%;
  padding: 0 10ch;
  display: flex;
  justify-content: center;

  text-align: center;
  font-weight: bold;
  font-size: 15px;

  ${bp.small`
    font-size: 20px;
  `};
`

const Tag = styled('div')`
  white-space: nowrap;
`

export const EndResult = ({ overrideableBallotState }: { overrideableBallotState: D.OverrideableBallotState }) => {
  const { intl } = useEnv()
  const { formatMessage } = intl

  const topR = caseBallotState<React.ReactNode>({
    pre: null,
    inProgress: null,
    trend: () => null,
    projection: () => null,
    finalResult: ({
      finalResult: {
        voterTurnout: { percent },
      },
    }) => {
      if (percent !== null) {
        return (
          <span>
            {percent
              ? addThinSpacePercentage(
                  formatMessage(commonDescriptors.voterTurnout, {
                    percent: unPercent(percent),
                  }),
                )
              : '\uFEFF'}
          </span>
        )
      } else {
        return null
      }
    },
  })(overrideableBallotState.ballotState)

  if (overrideableBallotState.outcome) {
    return <FederalTopParticipation>{topR}</FederalTopParticipation>
  }

  return caseBallotState({
    pre: <div>&#xfeff;</div>,
    inProgress: <></>,
    trend: () => <div>&#xfeff;</div>,
    projection: () => <></>,
    finalResult: ({
      finalResult: {
        voterTurnout: { percent },
      },
    }) => {
      return (
        <FederalTopParticipation>
          <span>
            {percent
              ? addThinSpacePercentage(
                  formatMessage(commonDescriptors.voterTurnout, {
                    percent: unPercent(percent),
                  }),
                )
              : '\uFEFF'}
          </span>
        </FederalTopParticipation>
      )
    },
  })(overrideableBallotState.ballotState)
}

const EndResultContainer = styled('div')`
  font-family: var(--font-sans-serif);
  font-size: 15px;
  line-height: 23px;
  height: 20px;
  display: flex;
  align-items: center;
`

const LiveUpdateContainer = styled('div')`
  margin-right: 10px;
`

const FederalTopParticipation = styled('span')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--font-sans-serif);
  font-size: 15px;
  line-height: 23px;
  height: 20px;
`

const FederalTopParticipationIcon = styled('span')`
  svg {
    display: inline;
    color: var(--text-color);
    margin-right: 8px;
  }
`
